import { motion } from "framer-motion";
import { Brain, GraduationCap, Rocket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/contexts/LanguageContext";

export type PrimaryInterest = "mental-health" | "education" | "entrepreneurship";

interface LensStepProps {
  selected: PrimaryInterest | null;
  onSelect: (interest: PrimaryInterest) => void;
  onNext: () => void;
  onBack: () => void;
}

export function LensStep({ selected, onSelect, onNext, onBack }: LensStepProps) {
  const { t } = useTranslation();

  const interests: { id: PrimaryInterest; labelKey: string; descriptionKey: string; icon: typeof Brain }[] = [
    {
      id: "mental-health",
      labelKey: "onboarding.lens.mentalHealth",
      descriptionKey: "onboarding.lens.mentalHealthDesc",
      icon: Brain,
    },
    {
      id: "education",
      labelKey: "onboarding.lens.education",
      descriptionKey: "onboarding.lens.educationDesc",
      icon: GraduationCap,
    },
    {
      id: "entrepreneurship",
      labelKey: "onboarding.lens.entrepreneurship",
      descriptionKey: "onboarding.lens.entrepreneurshipDesc",
      icon: Rocket,
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      transition={{ duration: 0.4 }}
      className="space-y-8"
    >
      <div className="text-center space-y-2">
        <div className="mx-auto w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mb-4">
          <Brain className="w-8 h-8 text-accent" />
        </div>
        <h2 className="text-2xl font-bold text-foreground">{t('onboarding.lens.title')}</h2>
        <p className="text-muted-foreground">{t('onboarding.lens.description')}</p>
      </div>

      <div className="grid gap-4">
        {interests.map((interest, index) => {
          const Icon = interest.icon;
          const isSelected = selected === interest.id;

          return (
            <motion.button
              key={interest.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              onClick={() => onSelect(interest.id)}
              className={cn(
                "w-full p-6 rounded-xl border-2 text-left transition-all duration-300",
                "hover:scale-[1.02] hover:shadow-lg",
                isSelected
                  ? "border-primary bg-primary/5 shadow-md"
                  : "border-border bg-card hover:border-primary/50"
              )}
            >
              <div className="flex items-start gap-4">
                <div
                  className={cn(
                    "w-12 h-12 rounded-lg flex items-center justify-center shrink-0 transition-colors",
                    isSelected ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                  )}
                >
                  <Icon className="w-6 h-6" />
                </div>
                <div className="space-y-1">
                  <h3 className="font-semibold text-lg text-foreground">{t(interest.labelKey)}</h3>
                  <p className="text-sm text-muted-foreground">{t(interest.descriptionKey)}</p>
                </div>
              </div>
            </motion.button>
          );
        })}
      </div>

      <div className="flex gap-3">
        <Button variant="outline" onClick={onBack} className="flex-1">
          {t('onboarding.back')}
        </Button>
        <Button onClick={onNext} disabled={!selected} className="flex-1">
          {t('onboarding.continue')}
        </Button>
      </div>
    </motion.div>
  );
}
